import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { keepPreviousData } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { groupBy, isEmpty } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { QUERY_KEYS, SECOND, USED_FOR } from '../../constants';
import { useMarketCategories } from '../useMarketCategories';
import { usePublishedEvents } from '../../queries';
import { SelectedData } from '../../components/molecules/MarketGroupCategories';
import { flattenTournamentMarketGroups, resolveTournamentMarketGroup } from '../../helpers/tournamentMarketGroups';

type UseTournamentMatchesOptions = {
  enableMarketGroupsFeature?: boolean;
};

export const useTournamentMatches = (options: UseTournamentMatchesOptions = {}) => {
  const { enableMarketGroupsFeature = true } = options;
  const { sportId, tournamentId } = useParams();
  const enableMarketGroups = useFeatureIsOn('enable-tournament-market-groups') && enableMarketGroupsFeature;

  const { data: groupCategories, isLoading: isLoadingGroupCategories } = useMarketCategories(
    enableMarketGroups,
    USED_FOR.PRE_MATCH,
    sportId
  );

  const [selectedData, setSelectedData] = useState<SelectedData | undefined>();
  const marketGroupOptions = useMemo(() => flattenTournamentMarketGroups(groupCategories), [groupCategories]);
  const resolvedSelectedGroup = useMemo(
    () => resolveTournamentMarketGroup(groupCategories, selectedData?.id),
    [groupCategories, selectedData?.id]
  );

  useEffect(() => {
    if (!enableMarketGroups) return;
    if (!resolvedSelectedGroup) return;
    if (selectedData?.id === resolvedSelectedGroup.id) return;

    setSelectedData(resolvedSelectedGroup);
  }, [enableMarketGroups, resolvedSelectedGroup, selectedData?.id]);

  const {
    data: publishedEvents,
    isLoading,
    isFetching,
  } = usePublishedEvents({
    queryKey: [QUERY_KEYS.featuredEvents, tournamentId, resolvedSelectedGroup?.id],
    queryParams: {
      page: 1,
      limit: 100,
      tournamentIds: [tournamentId],
      sportId,
      marketTypeGroupId: resolvedSelectedGroup?.id,
    },
    options: {
      enabled:
        !!tournamentId && !!sportId && (!enableMarketGroups || !groupCategories?.length || !!resolvedSelectedGroup?.id),
      refetchInterval: 30 * SECOND,
      placeholderData: keepPreviousData,
    },
  });

  const eventsGroupedByDate = groupBy(publishedEvents?.items, (event) => dayjs(event.startDate).format('YYYY-MM-DD'));

  return {
    sportId,
    tournamentId,
    enableMarketGroups,
    groupCategories,
    marketGroupOptions,
    isLoadingGroupCategories,
    selectedData: resolvedSelectedGroup,
    setSelectedData,
    publishedEvents,
    isLoading,
    isFetching,
    eventsGroupedByDate,
    isEmpty: isEmpty(eventsGroupedByDate) && !isLoading,
  };
};
